import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import {
    AnnualReportProvider,
    AnnualReportTemplatesProvider,
    DEFAULT_SWISS_DISPLAY_OPTIONS,
    FixedAssetsScheduleValidationService,
    IAnnualReportTemplateSettings,
    IEqualityCheckPrintPreviewTableData,
    IItemForCalculatingRatioOption,
    INotesDetails,
    ISectionData,
    PRINT_OPTIONS_SWISS_VISIBILITY,
    PrintOptionsVisibility,
    PrintUsingPdfTronDto,
    SectionIds,
} from '@modeso/aumico-lib-annualreport-fe';
import {
    AumicoCoreLayoutProvider,
    ConfigurationsProvider,
    DateLocalePipe,
    EMPTY_ARRAY_LENGTH,
    EditSectionTitleComponent,
    LOCALE_LOCALIZATION_KEYS_MAP,
} from '@modeso/aumico-lib-core-fe';
import { NotesEmptyLinesRemovalHelper } from '@modeso/aumico-lib-notes-fe';
import { SettingsProvider } from '@modeso/aumico-lib-settings-fe';
import { TemplatesProvider } from '@modeso/aumico-lib-templates-fe';
import {
    AnnualReportStep,
    IAnnualReport,
    IDisplayOptions,
    IPrintOptions,
    ISignatureOptions,
    IStyleOptions,
    ITranslations,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { NotesNumberingHelper } from 'projects/aumico-lib-notes-fe/src/lib/helpers/notes-numbering.helper';
import { AppStateDispatcher } from 'projects/aumico-project-reports/src/app/state/app.state.dispatcher';
import { filter, map, tap } from 'rxjs';
import { getCompanyLogo } from '../../../../../../../aumico-lib-companies-fe/src/lib/actions/companies.actions';
import { NotificationsSocketService } from '../../../../../../../aumico-lib-core-fe/src/lib/services/notifications-socket.service';
import { PrintPreviewHelper } from '../../helpers/print-preview.helper';
import { AnnualReportPrintPage } from '../annualReportPrint.page';
const debugLang = Debug('aumico:SwissPrintPreviewPage//language');
@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-swiss-print-preview-page',
    templateUrl: 'swiss-print-preview.page.html',
    styleUrls: ['swiss-print-preview.page.scss'],
    providers: [FixedAssetsScheduleValidationService],
})
export class SwissPrintPreviewPage extends AnnualReportPrintPage implements OnInit, OnChanges, AfterViewInit {
    @ViewChild('aopSnapshot', { static: false }) aopSnapshotRef: ElementRef;
    @ViewChild(MatTabGroup) tabGroup: MatTabGroup | undefined;
    @ViewChild('aopSnapshotCurrentYear', { static: false }) aopSnapshotCurrentYearRef: ElementRef;

    printOptionsVisibility: PrintOptionsVisibility = PRINT_OPTIONS_SWISS_VISIBILITY;
    defaultSwissDisplayOptions = DEFAULT_SWISS_DISPLAY_OPTIONS;
    currentActiveLocale: string;
    LocaleLocalizationKeysMap = LOCALE_LOCALIZATION_KEYS_MAP;

    @Input() override orderedSectionsIds: SectionIds[] = [];
    @Input() override orderedSectionsTitles: SectionIds[] = [];
    @Input() override notesDetails: INotesDetails[] = [];
    @Input() override incomeItemsOptionsForCalculatingRatio: IItemForCalculatingRatioOption[] = [];
    @Input() override reportLocale: string;
    @Input() reportType: string | undefined;

    @Input() override equalityCheckDataCurrentYear: IEqualityCheckPrintPreviewTableData;
    @Input() override equalityCheckDataPreviousYear: IEqualityCheckPrintPreviewTableData | null;
    @Input() override noPreviousData = false;

    @Input() override printOptionsData: IPrintOptions | undefined;
    @Input() override disableShowEmsToggle = true;

    @Input() override previousDateTitleDefaultValue: string | undefined;
    @Input() override currentDateTitleDefaultValue: string | undefined;

    @Input() complementaryReports: ISectionData[] = [];
    @Input() override complementaryReportsAtTheStart: ISectionData[] = [];
    @Input() override complementaryReportsAtTheEnd: ISectionData[] = [];
    @Input() override complementaryReportsAsAppendix: ISectionData[] = [];

    public appendixSectionTitle: string | undefined;

    public showComplementaryReportsInOldLocation = true;

    @Input() override printPreviewLocalesAvailable: string[] = [];
    private _predifinedLabels: Map<string, string>;
    private mainTitle = new Map<string, string>();
    private subTitle = new Map<string, string>();
    reportMainTitle: string;
    reportSubTitle: string;
    companyLogo: string | ArrayBuffer;
    showCompanyLogo: boolean;

    @Output()
    companyLogoBase64 = new EventEmitter<string | ArrayBuffer>();

    @Input()
    set predifinedLabels(value: Map<string, string>) {
        this._predifinedLabels = value;
        debugLang('predifinedLabels', value);
        if (value != null) {
            setTimeout(() => {
                this.resetMainTitle(value);
            });

            setTimeout(() => {
                this.resetSubTitle(value);
            });
        }

        this.appendixSectionTitle = value.get('aumico_annual_report_print_preview_appendix_section_title');
    }

    get predifinedLabels(): Map<string, string> {
        return this._predifinedLabels;
    }

    @Input() set arNavigations(value: AnnualReportStep[]) {
        this.adjustPrintOptionsVisibilityAccordingToNavigation(value);

        this.adjustDefaultPrintOptionsValuesAccordingToNavigation(value);
    }

    @Input() set arTemplateSettings(value: IAnnualReportTemplateSettings | null) {
        if (value == null) {
            return;
        }

        this.adjustPrintOptionsVisibilityAccordingToArTemplateSettings(value);
    }

    private defaultPrintOptions: IPrintOptions | undefined;

    public aopSnapshotHtmlContent: string | undefined;
    public aopSnapshotCurrentYearHtmlContent: string | undefined;
    // Last snapshot HTML dispatched to the placeholder store. Used to detect whether a
    // re-capture actually changed the snapshot, so we only trigger a complementary-report
    // rebuild (handlePrintPreview) when needed — avoids redundant passes on initial load
    // and on print-option changes that don't affect the AOP snapshot.
    private lastAopSnapshotHtmlContent: string | null | undefined = undefined;
    private lastAopSnapshotCurrentYearHtmlContent: string | null | undefined = undefined;
    public appropriationOfProfitSection: ISectionData | undefined;
    public activeAnnualReport: IAnnualReport | undefined;

    public currentYear: string | undefined;
    public previousYear: string | undefined;

    @Output() printOptionsValueChanged = new EventEmitter<IPrintOptions>();
    @Output() printOptionsInitialValueReady = new EventEmitter<IPrintOptions>();
    @Output() onPrintAnnualReportUsingPdfTronClicked = new EventEmitter<void>();
    @Output() onExportExcelClicked = new EventEmitter<void>();
    @Output() onExportWordClicked = new EventEmitter<void>();
    @Output() onTabSelectionChanged = new EventEmitter<string>();
    @Output() aopSnapshotChanged = new EventEmitter<void>();
    @Output() aopSnapshotCurrentYearChanged = new EventEmitter<void>();
    @Output() onPrintComplementaryReportClick = new EventEmitter<{ index: number; fileId?: string }>();

    @Input() override isSealed: boolean = false;
    @Input() sealedAt: Date | null = null;
    @Input() sealedBy: string | null = null;
    @Input() hasSealHistory: boolean = false;
    @Output() sealClicked = new EventEmitter<void>();
    @Output() unsealClicked = new EventEmitter<void>();

    /** The page's applied-but-unsaved block; the preview's own inherited copy is not the parent's. */
    @Input() translationsBlock: ITranslations | undefined;
    @Input() override showTranslationButton: boolean = false;
    @Input() override translationGapCount: number = 0;
    @Input() override translationReviewOpen: boolean = false;
    @Output() manageTranslationClicked = new EventEmitter<void>();
    @Output() saveTranslationClicked = new EventEmitter<void>();
    @Output() translationLegendClicked = new EventEmitter<void>();
    @Output() auditLogClicked = new EventEmitter<void>();

    private resizeObserver: ResizeObserver | undefined;

    @HostListener('window:resize')
    onResize(): void {
        this.updateColumnHeight();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    constructor(
        protected override injector: Injector,
        protected override actionsListener$: ActionsSubject,
        protected override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override matDialog: MatDialog,
        protected override configuration: ConfigurationsProvider,
        protected override settingsProvider: SettingsProvider,
        protected override templateProvider: TemplatesProvider,
        protected override readonly i18next: I18NextService,
        protected override readonly annualReportTemplateProvider: AnnualReportTemplatesProvider,
        protected override readonly annualReportProvider: AnnualReportProvider,
        protected override readonly notesEmptyLinesRemovalHelper: NotesEmptyLinesRemovalHelper,
        protected override readonly notesNumberingHelper: NotesNumberingHelper,
        protected override readonly dateLocalePipe: DateLocalePipe,
        protected override appStateDispatcher: AppStateDispatcher,
        protected override readonly notificationsSocketService: NotificationsSocketService,
        private readonly el: ElementRef,
    ) {
        super(
            injector,
            actionsListener$,
            aumicoCoreLayoutProvider,
            matDialog,
            configuration,
            settingsProvider,
            templateProvider,
            i18next,
            annualReportTemplateProvider,
            annualReportProvider,
            notesEmptyLinesRemovalHelper,
            notesNumberingHelper,
            dateLocalePipe,
            appStateDispatcher,
            notificationsSocketService,
        );
        this.addSvgIcon('aumico-pattern-icon-edit-write');
        this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(
                filter(
                    (activeAnnualReport) => activeAnnualReport != null && activeAnnualReport.generalInformation != null,
                ),
                tap((activeAnnualReport) => {
                    const logo = activeAnnualReport.generalInformation?.company?.logo;
                    if (logo) {
                        this.companiesProvider.downloadCompanyLogo(logo.uuid);
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReport) => {
                this.activeAnnualReport = cloneDeep<IAnnualReport>(activeAnnualReport);
                this.printOptionsVisibility.displayOptions.hasHidePreviousYearToggle =
                    !activeAnnualReport.generalInformation.noPreviousData;
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['showTranslationButton'] != null || changes['translationReviewOpen'] != null) {
            setTimeout(() => this.tabGroup?._tabHeader?.updatePagination());
        }
        if (
            this.complementaryReportsAtTheStart.length !== EMPTY_ARRAY_LENGTH ||
            this.complementaryReportsAtTheEnd.length !== EMPTY_ARRAY_LENGTH ||
            this.complementaryReportsAsAppendix.length !== EMPTY_ARRAY_LENGTH
        ) {
            // if the user didn't save the new complementray reports so this means we need to show his
            // complementary reports in the old location which is base on the navigation order
            this.showComplementaryReportsInOldLocation = false;
        }
    }

    override ngOnInit(): void {
        this.currentActiveLocale = this.reportLocale;

        this.annualReportProvider
            .getPrintView$()
            .pipe(
                filter(
                    (printViewData: PrintUsingPdfTronDto) => printViewData != null && printViewData.data.aop != null,
                ),
                map((printViewData: PrintUsingPdfTronDto) => printViewData.data.aop),
                untilDestroyed(this),
            )
            .subscribe((data) => {
                this.appropriationOfProfitSection = cloneDeep<ISectionData>(data);
            });

        this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                if (data != null) {
                    if (data?.generalInformation?.balancePreviousYearDate) {
                        this.previousYear = new Date(data.generalInformation.balancePreviousYearDate)
                            .getFullYear()
                            .toString();
                    }
                    if (data?.generalInformation?.balanceSheetDate) {
                        this.currentYear = new Date(data.generalInformation.balanceSheetDate).getFullYear().toString();
                    }
                }
            });

        this.actionsListener$
            .pipe(ofType(getCompanyLogo.downloadsuccess), untilDestroyed(this))
            .subscribe(({ payload }) => {
                const extension = payload.type.split('/')[1];
                const logo = new File([payload], `logo.${extension}`, { type: payload.type });
                this.readFile(logo);
            });
    }

    private readFile(file: File): void {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            this.companyLogo = e.target?.result;
            this.companyLogoBase64.emit(this.companyLogo);
        };
        reader.readAsDataURL(file);
    }
    override ngAfterViewInit() {
        this.refreshAopSnapshots();
        this.resizeObserver = new ResizeObserver(() => this.updateColumnHeight());
        this.resizeObserver.observe(this.el.nativeElement);
    }

    /**
     * Capture both AOP snapshots from the (now re-rendered) hidden elements, dispatch them to
     * the placeholder store, and emit a single aopSnapshotChanged ONLY when a snapshot actually
     * changed. The parent rebuilds the complementary reports on that event so the embedded
     * @aop.snapshot reflects the current print options (e.g. aopHideDecimalPlacesValues). The
     * change guard keeps the common case (initial load / non-AOP option changes) to a single
     * handlePrintPreview() pass.
     */
    private refreshAopSnapshots(): void {
        const snapshotChanged = this.updateAopSnapshotHtmlContent();
        const currentYearSnapshotChanged = this.updateAopSnapshotCurrentYearHtmlContent();
        if (snapshotChanged || currentYearSnapshotChanged) {
            this.aopSnapshotChanged.emit();
        }
    }

    private updateAopSnapshotHtmlContent(): boolean {
        const snapshotHtml =
            this.aopSnapshotRef && this.activeAnnualReport?.appropriationOfProfit != null
                ? this.aopSnapshotRef.nativeElement.innerHTML
                : null;
        this.aopSnapshotHtmlContent = snapshotHtml ?? undefined;
        this.annualReportProvider.dispatchUpdateAopSnapshotPlaceholder(snapshotHtml);
        const changed = snapshotHtml !== this.lastAopSnapshotHtmlContent;
        this.lastAopSnapshotHtmlContent = snapshotHtml;
        return changed;
    }

    private updateAopSnapshotCurrentYearHtmlContent(): boolean {
        const snapshotHtml =
            this.aopSnapshotCurrentYearRef && this.activeAnnualReport?.appropriationOfProfit != null
                ? this.aopSnapshotCurrentYearRef.nativeElement.innerHTML
                : null;
        this.aopSnapshotCurrentYearHtmlContent = snapshotHtml ?? undefined;
        this.annualReportProvider.dispatchUpdateAopSnapshotCurrentYearPlaceholder(snapshotHtml);
        const changed = snapshotHtml !== this.lastAopSnapshotCurrentYearHtmlContent;
        this.lastAopSnapshotCurrentYearHtmlContent = snapshotHtml;
        return changed;
    }

    onPrintOptionsValueChanged(printOptions: IPrintOptions): void {
        // if there is no AOP in the report, we must set the placeSignatureAfterAop to false
        if (this.printOptionsVisibility?.displayOptions?.hasAopToggle === false) {
            printOptions.signatureOptions.placeSignatureAfterAop = false;
        }
        this.printOptionsDataToBeSaved = cloneDeep<IPrintOptions>(printOptions);
        this.printDisplayOptions = cloneDeep<IDisplayOptions>(printOptions.displayOptions);
        this.printStyleOptions = cloneDeep<IStyleOptions>(printOptions.styleOptions);
        this.printSignatureOptions = cloneDeep<ISignatureOptions>(printOptions.signatureOptions);

        if (this.predifinedLabels != null) {
            this.setTitles(this.predifinedLabels);

            const predefinedLocalizationKeyForMainTitle =
                PrintPreviewHelper.getPredefinedLocalizationKeyForReportMainTitle(this.reportType);

            // if predefinedLabel for report titles was set from localizations, then we need to update printOptionsInitialValue (DefaultValue)
            if (
                this.mainTitleDefaultValue === this.predifinedLabels.get(predefinedLocalizationKeyForMainTitle) &&
                this.defaultPrintOptions != null
            ) {
                this.defaultPrintOptions = {
                    ...this.defaultPrintOptions,
                    displayOptions: {
                        ...this.defaultPrintOptions.displayOptions,
                        mainTitle: this.mainTitleDefaultValue,
                    },
                };
                this.printOptionsInitialValueReady.emit(this.defaultPrintOptions);
            }

            if (
                this.subTitleDefaultValue ===
                    this.predifinedLabels.get('aumico_reports_annualreport_cover_label_title_2nd_prefix') &&
                this.defaultPrintOptions != null
            ) {
                this.defaultPrintOptions = {
                    ...this.defaultPrintOptions,
                    displayOptions: {
                        ...this.defaultPrintOptions.displayOptions,
                        subtitle: this.subTitleDefaultValue,
                    },
                };
                this.printOptionsInitialValueReady.emit(this.defaultPrintOptions);
            }
        }

        // Regenerate the print view with the new options synchronously so the visible AOP section
        // (and every other section) reflects the change immediately — no deferred macrotask delay.
        // This rebuilds the AOP section data (data.aop) and re-renders the hidden #aopSnapshot
        // element with the new display options (decimals, empty lines) and style options (colors).
        if (this.configuration.isStorybook()) {
            super.onPrintOptionsChanged(printOptions);
        } else {
            this.printOptionsValueChanged.emit(printOptions);
        }
        // Only AFTER Angular has re-rendered the snapshot with the regenerated AOP data do we
        // capture it and (if it changed) trigger a single complementary-report rebuild, so the
        // embedded @aop.snapshot reflects the current options instead of lagging one toggle behind.
        setTimeout(() => this.refreshAopSnapshots());
    }

    onInitialPrintOptionsValueReady(printOptions: IPrintOptions): void {
        this.defaultPrintOptions = cloneDeep<IPrintOptions>(printOptions);

        if (this.configuration.isStorybook()) {
            super.onInitialPrintOptionsReady(printOptions);
        } else {
            this.printOptionsInitialValueReady.emit(printOptions);
        }
    }

    onPrintAnnualReportUsingPdfTronClick(): void {
        this.onPrintAnnualReportUsingPdfTronClicked.emit();
    }

    onExportExcelClick(): void {
        this.onExportExcelClicked.emit();
    }

    onExportWordClick(): void {
        this.onExportWordClicked.emit();
    }

    protected adjustPrintOptionsVisibilityAccordingToNavigation(navigations: AnnualReportStep[]): void {
        const adjustments = PrintPreviewHelper.adjustPrintOptionsVisibilityAccordingToNavigation(navigations);

        this.printOptionsVisibility = {
            ...this.printOptionsVisibility,
            consistencyCheck: {
                ...this.printOptionsVisibility.consistencyCheck,
                ...adjustments.consistencyCheck,
            },
            styleOptions: {
                ...this.printOptionsVisibility.styleOptions,
                ...adjustments.styleOptions,
            },
            displayOptions: {
                ...this.printOptionsVisibility.displayOptions,
                ...adjustments.displayOptions,
            },
            signatureOptions: {
                ...this.printOptionsVisibility.signatureOptions,
                ...adjustments.signatureOptions,
            },
        };
    }

    public onTabSelectionChange($event: MatTabChangeEvent) {
        this.mainTitle[this.currentActiveLocale] = this.printOptionsDataToBeSaved?.displayOptions.mainTitle;
        this.subTitle[this.currentActiveLocale] = this.printOptionsDataToBeSaved?.displayOptions.subtitle;

        const activeLocale = this.printPreviewLocalesAvailable[$event.index];
        this.currentActiveLocale = activeLocale.toLowerCase();
        this.onTabSelectionChanged.emit(activeLocale);
    }

    private adjustDefaultPrintOptionsValuesAccordingToNavigation(navigations: AnnualReportStep[]): void {
        this.defaultSwissDisplayOptions = {
            ...this.defaultSwissDisplayOptions,
            showNotes: navigations.includes(AnnualReportStep.NOTES),
        };
    }

    private adjustPrintOptionsVisibilityAccordingToArTemplateSettings(
        arTemplateSettings: IAnnualReportTemplateSettings,
    ): void {
        this.printOptionsVisibility.displayOptions.hasShareholderInformationToggle =
            arTemplateSettings.showShareholderInfo;

        this.defaultSwissDisplayOptions = {
            ...this.defaultSwissDisplayOptions,
            showShareholderInformation: arTemplateSettings.showShareholderInfo,
        };
    }

    private setTitles(predifinedLabels: Map<string, string>): void {
        const predefinedLocalizationKeyForMainTitle = PrintPreviewHelper.getPredefinedLocalizationKeyForReportMainTitle(
            this.reportType,
        );

        this.mainTitleDefaultValue = this.printOptionsDataToBeSaved
            ? this.printOptionsDataToBeSaved.displayOptions.mainTitle
            : predifinedLabels.get(predefinedLocalizationKeyForMainTitle);
        this.subTitleDefaultValue = this.printOptionsDataToBeSaved
            ? this.printOptionsDataToBeSaved.displayOptions.subtitle
            : predifinedLabels.get('aumico_reports_annualreport_cover_label_title_2nd_prefix');

        if (!this.printOptionsDataToBeSaved) return;

        this.printOptionsDataToBeSaved = {
            ...this.printOptionsDataToBeSaved,
            displayOptions: {
                ...this.printOptionsDataToBeSaved.displayOptions,
                mainTitle: this.mainTitleDefaultValue,
                subtitle: this.subTitleDefaultValue,
            },
        };
    }

    private resetMainTitle(predifinedLables: Map<string, string>): void {
        if (this.currentActiveLocale == null) {
            this.currentActiveLocale = this.reportLocale;
        }

        if (this.reportLocale === this.currentActiveLocale) {
            this.mainTitle[this.reportLocale] =
                this.mainTitle[this.reportLocale] ?? this.printOptionsDataToBeSaved.displayOptions.mainTitle;
        }

        const predefinedLocalizationKeyForMainTitle = PrintPreviewHelper.getPredefinedLocalizationKeyForReportMainTitle(
            this.reportType,
        );

        this.mainTitleDefaultValue =
            this.mainTitle[this.currentActiveLocale] ?? predifinedLables.get(predefinedLocalizationKeyForMainTitle);
    }

    private resetSubTitle(predifinedLables: Map<string, string>): void {
        if (this.currentActiveLocale == null) {
            this.currentActiveLocale = this.reportLocale;
        }
        if (this.reportLocale === this.currentActiveLocale) {
            this.subTitle[this.reportLocale] =
                this.subTitle[this.reportLocale] ?? this.printOptionsDataToBeSaved.displayOptions.subtitle;
        }

        this.subTitleDefaultValue =
            this.subTitle[this.currentActiveLocale] ??
            predifinedLables.get('aumico_reports_annualreport_cover_label_title_2nd_prefix');
    }

    public onSectionTitleChanged(sectionTitle: string, sectionId: SectionIds): void {
        if (
            this.printOptionsDataToBeSaved.reportSectionsTitles != null &&
            this.printOptionsDataToBeSaved.reportSectionsTitles[sectionId] != null
        ) {
            this.printOptionsDataToBeSaved.reportSectionsTitles[sectionId][this.currentActiveLocale] = sectionTitle;
        } else {
            this.printOptionsDataToBeSaved.reportSectionsTitles = {
                ...this.printOptionsDataToBeSaved.reportSectionsTitles,
                [sectionId]: {
                    [this.currentActiveLocale]: sectionTitle,
                },
            };
        }

        this.printOptionsValueChanged.emit(this.printOptionsDataToBeSaved);
    }

    protected override addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    public editAppendixSectionTitle(): void {
        const dialogRef = this.matDialog.open<EditSectionTitleComponent, string, string | undefined>(
            EditSectionTitleComponent,
            {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: this.appendixSectionTitle,
            },
        );

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result != null) {
                    this.appendixSectionTitle = result;
                    this.onSectionTitleChanged(result, SectionIds.APPENDIX);
                }
            });
    }

    public onShowCompanyLogo(show: boolean): void {
        if (this.printOptionsDataToBeSaved.displayOptions) {
            this.printOptionsDataToBeSaved.displayOptions.showReportCompanyLogo = show;
        }

        this.showCompanyLogo = show;
    }

    onPrintComplementaryReportClicked(data: { index: number; fileId?: string }): void {
        this.onPrintComplementaryReportClick.emit(data);
    }

    private updateColumnHeight(): void {
        const FOOTER_HEIGHT = 64;
        const col: HTMLElement | null = this.el.nativeElement.querySelector('.print-preview');
        if (!col) return;
        const top = col.getBoundingClientRect().top;
        if (top <= 0) return; // element not yet laid out — skip until ResizeObserver fires again
        const height = window.innerHeight - top - FOOTER_HEIGHT - 12;
        this.el.nativeElement.style.setProperty('--scroll-table-height-print-preview', `${height}px`);
    }
}
